'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '../../lib/utils';

const TooltipText = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & React.ComponentProps<'div'>) => {
  return (
    <div
      className={cn('!text-caption-small text-comp-tooltip-text', className)}
      {...props}
    >
      {children}
    </div>
  );
};

const TooltipSubText = ({
  children,
  className,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
} & React.ComponentProps<'div'>) => {
  return (
    <div
      className={cn(
        '!text-caption-small text-sys-cont-neutral-disabled-light',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
};

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root
        data-slot="tooltip"
        {...props}
      />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      {...props}
    />
  );
}

function TooltipContent({
  className,
  sideOffset = 0,
  collisionPadding = 16,
  children,
  portalProps,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & {
  portalProps?: React.ComponentProps<typeof TooltipPrimitive.Portal>;
}) {
  return (
    <TooltipPrimitive.Portal {...portalProps}>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          'z-50 flex w-fit max-w-[var(--radix-popper-available-width)] origin-[var(--radix-tooltip-content-transform-origin)] gap-5xs rounded-2xs bg-comp-tooltip-bg px-2xs py-3xs !text-caption-small text-wrap break-words text-comp-tooltip-text animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 isolate',
          className,
        )}
        {...props}
      >
        <div className="relative z-10">{children}</div>
        <TooltipPrimitive.Arrow className="z-0 size-2.5 translate-y-[calc(-50%_-_3px)] rotate-45 rounded-[1px] bg-comp-tooltip-arrow fill-comp-tooltip-arrow" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export {
  Tooltip as RadixTooltip,
  TooltipContent as RadixTooltipContent,
  TooltipProvider as RadixTooltipProvider,
  TooltipTrigger as RadixTooltipTrigger,
  TooltipSubText,
  TooltipText,
};
